import { useNavigate } from "react-router-dom";
import {
  Shield,
  Ticket,
  Banknote,
  CreditCard,
  LogOut,
  LayoutDashboard,
  Mail,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

const adminNavItems = [
  { title: "Admin", url: "/admin", icon: Shield },
  { title: "Kumppanit", url: "/admin/partners", icon: Ticket },
  { title: "Bulk-lähetys", url: "/admin/partners/bulk", icon: Mail },
  { title: "Tilitykset", url: "/admin/partners/payouts", icon: Banknote },
  { title: "Stripe audit", url: "/admin/stripe-audit", icon: CreditCard },
] as const;

export default function AdminSidebar() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const displayName = profile?.name || user?.email?.split("@")[0] || "Käyttäjä";
  const displayEmail = user?.email || "";

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="p-4">
        <button
          className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left"
          onClick={() => navigate("/admin")}
          type="button"
        >
          <Logo dark size="md" className="shrink-0" />
          {!isCollapsed && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-sidebar-foreground/50">Portaali</span>
                <Badge variant="secondary" className="h-5 rounded-lg px-2 text-[10px]">ADMIN</Badge>
              </div>
              <span className="text-[12px] font-semibold text-sidebar-foreground">Kilometrit.fi</span>
            </div>
          )}
        </button>
      </SidebarHeader>

      <Separator className="mx-3 w-auto opacity-20" />

      <SidebarContent className="pt-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-widest font-semibold">
            Ylläpito
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNavItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      className="text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-xl"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-widest font-semibold">
            Sovellus
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Dashboard">
                  <NavLink
                    to="/dashboard"
                    className="text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-xl"
                    activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Dashboard</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3">
        <Separator className="mb-2 opacity-20" />
        <div className="flex items-center gap-3 rounded-xl p-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary/20 text-sm font-bold text-sidebar-primary">
            {displayName.charAt(0).toUpperCase()}
          </div>
          {!isCollapsed && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <span className="truncate text-sm font-medium text-sidebar-foreground">{displayName}</span>
              <span className="truncate text-[11px] text-sidebar-foreground/40">{displayEmail}</span>
            </div>
          )}
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              onClick={handleLogout}
              title="Kirjaudu ulos"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
